import { createSelector, Selector } from "@reduxjs/toolkit";

import { isNot, isUndefined } from "../../../utilities/typeGuardUtils";

import { ModulFState } from "../store";

import { RuntimeStammdatenManifest, StammdatenManifest, StammdatenSlice } from "./types";

export const getSDManifestMap: Selector<ModulFState, StammdatenSlice["manifests"], never> = state =>
	state.stammdaten.manifests;

export function getSDManifest(sdID: string | undefined): Selector<ModulFState, StammdatenManifest | undefined, never> {
	if (!sdID) {
		return () => undefined;
	}

	return state => state.stammdaten.manifests[sdID];
}

export const getSDManifests: Selector<ModulFState, RuntimeStammdatenManifest[], never> = createSelector(
	[getSDManifestMap],
	sdMap => Object.values(sdMap).filter(isNot(isUndefined))
);

export const getLatestSDManifests: Selector<ModulFState, RuntimeStammdatenManifest[], never> = createSelector(
	[getSDManifestMap],
	sdMap => {
		const latestVersions: Record<string, RuntimeStammdatenManifest> = {};
		Object.values(sdMap).forEach(manifest => {
			if (!manifest) {
				return;
			}
			const key = manifest.identification.key;
			if (manifest.identification.version > (latestVersions[key]?.identification.version ?? 0)) {
				latestVersions[key] = manifest;
			}
		});
		return Object.values(latestVersions);
	}
);

/**
 * Returns the latest-version manifest that shares the same application key as the manifest
 * identified by the given appId.
 */
export function getLatestSDManifestByAppId(
	appId: string
): Selector<ModulFState, RuntimeStammdatenManifest | undefined, never> {
	return createSelector([getSDManifestMap, getLatestSDManifests], (sdMap, latestManifests) => {
		const key = sdMap[appId]?.identification.key;
		if (!key) {
			return undefined;
		}
		return latestManifests.find(m => m.identification.key === key);
	});
}
